import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import CopiedToast from "./CopiedToast";

const ShareModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 6000;
  min-width: 350px;
  max-width: 90vw;
`;

const ShareModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 5999;
`;

const ShareModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ShareModalTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 500;
  color: black;
`;

const ShareModalCloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  font-size: 16px;
  font-weight: bold;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const ShareTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 0.75rem;
`;

const ShareTab = styled.button<{ active?: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  color: ${(props) => (props.active ? "#1a73e8" : "#666")};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-bottom: 2px solid
    ${(props) => (props.active ? "#1a73e8" : "transparent")};
  margin-bottom: -0.8rem;

  &:hover {
    color: #1a73e8;
  }
`;

const ShareLinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ShareLinkInput = styled.input`
  flex: 1;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  outline: none;
  color: #1a73e8;
`;

const ShareCopyButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0.5rem;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const QRCodeContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  tab: "link" | "qr";
  setTab: (t: "link" | "qr") => void;
  url: string;
};

export default function Share({ open, onClose, tab, setTab, url }: Props) {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => setIsCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isCopied]);

  if (!open) return null;

  return (
    <>
      <ShareModalOverlay onClick={onClose} />
      <ShareModal>
        <ShareModalHeader>
          <ShareModalTitle>
            {t("Zdieľať odkaz na budovu") || "Share building link"}
          </ShareModalTitle>
          <ShareModalCloseButton type="button" onClick={onClose}>
            ✕
          </ShareModalCloseButton>
        </ShareModalHeader>
        <ShareTabs>
          <ShareTab
            type="button"
            active={tab === "link"}
            onClick={() => setTab("link")}
          >
            🔗 {t("Odkaz") || "Link"}
          </ShareTab>
          <ShareTab
            type="button"
            active={tab === "qr"}
            onClick={() => setTab("qr")}
          >
            📱 {t("QR Code") || "QR Code"}
          </ShareTab>
        </ShareTabs>

        {tab === "link" && (
          <ShareLinkRow>
            <ShareLinkInput type="text" value={url} readOnly />
            <ShareCopyButton
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(url);
                setIsCopied(true);
              }}
              title={t("copy") || "Kopírovať"}
            >
              📋
            </ShareCopyButton>
          </ShareLinkRow>
        )}

        {tab === "qr" && (
          <QRCodeContainer>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
                url,
              )}`}
              alt="QR Code"
            />
          </QRCodeContainer>
        )}
      </ShareModal>
      <CopiedToast visible={isCopied} message={t("buildingLinkCopied") as string} />
    </>
  );
}
